import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FileText, Clock, Send, CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { ActionPanels, needsFollowUp, daysSince } from "@/components/dashboard/ActionPanels";
import { api, errMsg, money, fmtDate } from "@/lib/api";
import { STATUS_LABEL } from "@/lib/types";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BulkDeleteDialog } from "@/components/BulkDeleteDialog";

function Stat({ label, value, icon: Icon, testid }: any) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between"><span className="text-sm font-medium text-gray-500">{label}</span><Icon size={18} className="text-blue-700" /></div>
      <div className="mt-2 truncate text-xl font-bold text-gray-900 sm:text-2xl">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const { user } = useAuth() as any;
  const [quotes, setQuotes] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [homeowners, setHomeowners] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [form, setForm] = useState({ homeowner_id: "", template_id: "", title: "" });

  const load = () => api.get("/quotes").then((r) => { setQuotes(r.data); setLoaded(true); });
  useEffect(() => { load(); }, []);

  const openCreate = async () => {
    const [h, t] = await Promise.all([api.get("/homeowners"), api.get("/templates")]);
    setHomeowners(h.data); setTemplates(t.data);
    setForm({ homeowner_id: h.data[0]?.id || "", template_id: t.data[0]?.id || "", title: "" });
    setOpen(true);
  };

  const create = async () => {
    if (!form.homeowner_id) return toast.error("Select a homeowner first");
    try {
      const { data } = await api.post("/quotes", { ...form, template_id: form.template_id || null, title: form.title || null });
      toast.success("Quote created"); nav(`/quotes/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Delete this quote and its history?")) return;
    try { await api.delete(`/quotes/${id}`); toast.success("Quote deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const shown = quotes.filter((q) => filter === "all" || q.status === filter);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const shownSel = shown.filter((q) => sel.has(q.id));
  const allSelected = shown.length > 0 && shownSel.length === shown.length;
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(shown.map((q) => q.id)));
  const PROTECTED = ["sent", "viewed", "accepted", "revision_requested"];
  const [bulkOpen, setBulkOpen] = useState(false);
  const removeSelected = async (includeProtected: boolean) => {
    const targets = shownSel.filter((q) => includeProtected || !PROTECTED.includes(q.status));
    const results = await Promise.allSettled(targets.map((q) => api.delete(`/quotes/${q.id}`)));
    const failed = results.filter((r) => r.status === "rejected").length;
    const skipped = shownSel.length - targets.length;
    if (failed) toast.error(`${failed} quote${failed > 1 ? "s" : ""} could not be deleted`); else toast.success(`Deleted ${targets.length} quote${targets.length > 1 ? "s" : ""}${skipped ? ` · skipped ${skipped} sent/accepted` : ""}`);
    setSel(new Set()); load();
  };
  const acceptedValue = quotes.filter((q) => q.status === "accepted").reduce((s, q) => s + q.total, 0);
  const isAdmin = user?.role === "admin";
  const pipelineValue = quotes.filter((q) => ["sent", "viewed"].includes(q.status)).reduce((s, q) => s + q.total, 0);

  return (
    <AppShell title={isAdmin ? "Quotes" : "My Quotes"} actions={<Button data-testid="new-quote-btn" onClick={openCreate} className="bg-blue-700 hover:bg-blue-800"><Plus size={16} className="mr-1.5" /> New Quote</Button>}>
      {!isAdmin && <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 px-5 py-3 text-sm text-blue-900" data-testid="rep-welcome">Welcome back, {user?.name}. You see only your own quotes here. Drafts need admin approval before they can be sent.</div>}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        <Stat label={isAdmin ? "Total quotes" : "My quotes"} value={quotes.length} icon={FileText} testid="stat-total" />
        <Stat label={isAdmin ? "Awaiting approval" : "Waiting for approval"} value={quotes.filter((q) => q.status === "internal_review").length} icon={Clock} testid="stat-review" />
        <Stat label="Out with homeowners" value={`${quotes.filter((q) => ["sent", "viewed"].includes(q.status)).length} · ${money(pipelineValue)}`} icon={Send} testid="stat-sent" />
        <Stat label="Accepted value" value={money(acceptedValue)} icon={CheckCircle2} testid="stat-accepted" />
      </div>
      {loaded && <ActionPanels quotes={quotes} isAdmin={isAdmin} />}

      <div className="mt-8 rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-5 py-3">
          {["all", ...Object.keys(STATUS_LABEL)].map((s) => (
            <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)} className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${filter === s ? "bg-blue-700 text-white" : "text-gray-600 hover:bg-gray-100"}`}>{s === "all" ? "All" : STATUS_LABEL[s]}</button>
          ))}
          {isAdmin && shownSel.length > 0 && <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-gray-500" data-testid="quotes-selected-count">{shownSel.length} selected</span>
            <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="quotes-bulk-delete-btn" onClick={() => setBulkOpen(true)}><Trash2 size={14} className="mr-1" /> Delete selected</Button>
            <BulkDeleteDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onConfirm={removeSelected} noun="quotes" testid="quotes-bulk-delete"
              safeCount={shownSel.filter((q) => !PROTECTED.includes(q.status)).length} protectedCount={shownSel.filter((q) => PROTECTED.includes(q.status)).length} protectedLabel="sent, viewed or accepted" />
          </div>}
        </div>
        <div className="overflow-x-auto"><table className="w-full text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>{isAdmin && <th className="w-10 px-4 py-3"><input type="checkbox" aria-label="Select all quotes" data-testid="quotes-select-all" className="h-4 w-4 cursor-pointer accent-blue-700" checked={allSelected} onChange={toggleAll} /></th>}<th className="px-5 py-3 font-medium">Quote</th><th className="px-5 py-3 font-medium">Homeowner</th><th className="px-5 py-3 font-medium">Status</th><th className="px-5 py-3 font-medium">Version</th><th className="px-5 py-3 text-right font-medium">Total</th><th className="px-5 py-3 font-medium">Updated</th><th className="px-5 py-3" /></tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {shown.map((q) => (
              <tr key={q.id} className={`cursor-pointer ${sel.has(q.id) ? "bg-blue-50/60" : "hover:bg-gray-50"}`} data-testid={`quote-row-${q.id}`} onClick={() => nav(`/quotes/${q.id}`)}>
                {isAdmin && <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label="Select quote" data-testid={`quote-select-${q.id}`} className="h-4 w-4 cursor-pointer accent-blue-700" checked={sel.has(q.id)} onChange={() => toggle(q.id)} /></td>}
                <td className="px-5 py-3 font-medium text-gray-900">{q.title}</td>
                <td className="px-5 py-3 text-gray-600">{q.homeowner_name}<div className="text-xs text-gray-400">{q.homeowner_address}</div></td>
                <td className="px-5 py-3"><StatusBadge status={q.status} />{needsFollowUp(q) && <span className="ml-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800" data-testid={`follow-up-badge-${q.id}`}>No view · {daysSince(q.sent_at || q.updated_at)}d</span>}</td>
                <td className="px-5 py-3 text-gray-600">v{q.current_version}</td>
                <td className="px-5 py-3 text-right font-medium text-gray-900">{money(q.total)}</td>
                <td className="px-5 py-3 text-gray-500">{fmtDate(q.updated_at)}</td>
                <td className="px-5 py-3 text-right">{user?.role === "admin" && <button data-testid={`delete-quote-${q.id}`} onClick={(e) => { e.stopPropagation(); remove(q.id); }} className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>}</td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={isAdmin ? 8 : 7} className="px-5 py-12 text-center text-gray-500" data-testid="quotes-empty">No quotes yet. Create your first proposal.</td></tr>}
          </tbody>
        </table></div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-quote-dialog">
          <DialogHeader><DialogTitle>New Quote</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Homeowner / Project</Label>
              <select data-testid="new-quote-homeowner-select" className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm" value={form.homeowner_id} onChange={(e) => setForm({ ...form, homeowner_id: e.target.value })}>
                {homeowners.length === 0 && <option value="">No homeowners — add one first</option>}
                {homeowners.map((h) => <option key={h.id} value={h.id}>{h.name} — {h.address}</option>)}
              </select></div>
            <div><Label>Proposal template</Label>
              <select data-testid="new-quote-template-select" className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm" value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value })}>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.pages?.length || 0} pages)</option>)}
                <option value="">Blank (cover + pricing only)</option>
              </select></div>
            <div><Label>Title (optional)</Label><Input data-testid="new-quote-title-input" className="mt-1" placeholder="Auto-generated from homeowner" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <Button data-testid="new-quote-submit-btn" onClick={create} className="w-full bg-blue-700 hover:bg-blue-800">Create & open builder</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
